import { FC, useEffect, useState } from 'react';
import {
  Button,
  Form,
  FormField,
  Heading,
  Input,
  Loader,
  Text,
} from '@livechat/design-system-react-components';

import Config from 'Config';
import { StandardError } from 'types/error';
import { Layout } from 'components/layout/Layout';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { JoinLinkExpired } from 'components/error-message/JoinLinkExpired';
import { ErrorMessageForm } from 'components/forms/error-message-form/ErrorMessageForm';
import { PasswordRequirements } from 'components/password-requirements/PasswordRequirements';
import { FormStates, SigninFormData } from 'types/form';
import { fetchTokenDetails } from 'hooks/use-get-token-details';
import Amplitude from 'services/Amplitude';
import Location from 'services/Location';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { accountsClient } from 'services/connectivity/accounts/client';
import { isEmailValid } from 'helpers/validators/email';
import { validatePassword } from 'helpers/validators/password';
import { submitPost } from 'helpers/form';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { getAppLinkByName, getProductName } from 'helpers/product';
import { Product } from 'types/product';

import SignupLegalInfo from '../signup/components/SignupLegalInfo';

import * as styles from './styles';

export const ActivateAccount: FC = () => {
  const [formState, setFormState] = useState<string>(FormStates.Default);
  const [formData, setFormData] = useState<SigninFormData>({
    email: isEmailValid(Location.getNthUrlParam(1)) ? Location.getNthUrlParam(1) : '',
  });
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
  const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
  const [isProcessingToken, setIsProcessingToken] = useState<boolean>(true);
  const [requestError, setRequestError] = useState<StandardError | null>(null);

  const accessToken = Location.getNthUrlParam(2);

  useEffect(() => {
    const checkAccessToken = async () => {
      try {
        await fetchTokenDetails(accessToken);
        setIsTokenValid(true);
        setIsProcessingToken(false);
      } catch (error) {
        setIsTokenValid(false);
        setIsProcessingToken(false);
      }
    };

    checkAccessToken();
  }, []);

  const handlePasswordInputChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      password: e.target.value.trim(),
    }));

    if (!validatePassword(e.target.value.trim())) {
      setPasswordValidationError(null);
      setFormState(FormStates.Default);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState(FormStates.Requesting);
    const passwordValidation = validatePassword(formData.password);

    if (passwordValidation) {
      setPasswordValidationError(passwordValidation);
      setFormState(FormStates.Default);

      return;
    }

    const { error } = await accountsClient.resetPassword(
      { password: formData.password },
      accessToken
    );

    if (error) {
      if (
        error.error === 'access_denied' &&
        error.error_description === 'account has credentials authentication disabled'
      ) {
        Location.handleCustomSSORedirect();
        return;
      }
      setRequestError({
        error: error.error,
        description: error.error_description,
        requestId: error.request_id,
      });
      setFormState(FormStates.Error);

      return;
    }

    setFormState(FormStates.Success);
    Amplitude.logEvent(formData.email, 'Agents: Agent activated via email');
    pushExternalRedirectToDataLayer(getAppLinkByName(getProductName(Product.LiveChat)));
    submitPost(`${Config.accounts_api_url}/?${buildOauthQuery(Config)}`, {
      email: formData.email,
      password: formData.password,
    });
  };

  if (!formData.email || !accessToken) {
    return (
      <ErrorMessageForm
        titleCaption="Incorrect link"
        titleContent={
          <>
            Your activation link is incorrect
            <br /> Chat with our support team to get help.
          </>
        }
        contactSupportButton
        backToLogin
      />
    );
  }

  if (!isTokenValid) {
    return <JoinLinkExpired />;
  }

  if (requestError) {
    return (
      <UnexpectedError requestId={requestError.requestId} description={requestError.description} />
    );
  }

  if (formState === FormStates.Success) {
    return (
      <div css={styles.successContainer}>
        <Heading size="lg">All done!</Heading>
        <Text>Redirecting to the app…</Text>
      </div>
    );
  }

  if (isProcessingToken) {
    return (
      <div css={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <Layout showHeaderLogo showAlreadyHaveAnAccount>
      <div css={styles.container}>
        <Heading size="lg" css={styles.header}>
          Join your team on LiveChat
        </Heading>
        <Form onSubmit={handleSubmit}>
          <FormField labelText="Business email" labelFor="email" css={styles.formField}>
            <Input
              type="email"
              name="email"
              placeholder="name@work-email.com"
              value={formData.email}
              inputSize="large"
              disabled
              css={styles.disabledInput}
            />
          </FormField>

          <FormField
            labelText="Password"
            labelFor="password"
            css={styles.passwordField}
            error={passwordValidationError}
          >
            <Input
              type="password"
              name="password"
              placeholder="Set your password"
              inputSize="large"
              onChange={handlePasswordInputChange}
              error={!!passwordValidationError}
              autoFocus
            />
          </FormField>
          <PasswordRequirements
            password={formData.password}
            showErrors={!!passwordValidationError}
          />

          <div>
            <Button
              type="submit"
              kind="destructive"
              size="large"
              loading={formState === FormStates.Requesting}
              css={styles.button}
            >
              Create account
            </Button>
            <SignupLegalInfo />
          </div>
        </Form>
      </div>
    </Layout>
  );
};
